import React from "react";
import { PropTypes } from "prop-types";
import { MrbButton } from "mrb/components/button";
import { MrbSelect } from "mrb/components/select";
import { MrbToggle } from "mrb/components/checkbox";
import { Image } from "common/components";
import { observer } from "mobx-react";
import { useRootStore } from "mrb/core/contexts";
import { map, isNil } from "lodash";

function LoadioStoreHomeSectionTemplate({ loadioStoreHomeSectionViewStore, t }) {
    return <EditPanelContainer loadioStoreHomeSectionViewStore={loadioStoreHomeSectionViewStore} t={t} />;
}

const EditPanelContainer = observer(function EditPanelContainer({ loadioStoreHomeSectionViewStore, t }) {
    return <EditPanel loadioStoreHomeSectionViewStore={loadioStoreHomeSectionViewStore} t={t} />;
});

const EditPanel = observer(function EditPanel({ loadioStoreHomeSectionViewStore, t }) {
    const {
        item,
        isSystemSection,
        setIsActive,
        onTitleChange,
        contentSelectStore,
        onSelectItem,
        isAddContentButtonDisabled,
        deleteSection,
        selectedItemExists,
    } = loadioStoreHomeSectionViewStore;
    return (
        <div className="c-device-menu__item c-device-menu__item--secondary u-mar--bottom--tny">
            <div className="u-width--100">
                <div className="c-device__header">
                    <TitleInput item={item} onChange={onTitleChange} />
                    <MrbToggle
                        label="Visible in the App"
                        labelClassNameExtend=" u-mar--top--tny"
                        id={`section-${item.id}`}
                        value={item.isActive}
                        onChange={(event) => setIsActive(event.target.checked)}
                    />

                    <div className="u-mar--left--auto u-display--flex u-gap--sml">
                        {!isSystemSection && (
                            <MrbButton
                                label="Delete"
                                className="c-btn c-btn--base c-btn--ghost"
                                onClick={deleteSection}
                            />
                        )}
                    </div>
                </div>

                {!isSystemSection && (
                    <div>
                        <div className="u-separator--primary"></div>
                        <div className="c-device__header">
                            <MrbSelect
                                store={contentSelectStore}
                                disabled={!item.isActive}
                                formatOptionLabel={({ item }) => <OptionLabel item={item} />}
                                classNameExtend="c-dropdown--dark c-dropdown--base u-width--400--px"
                            />
                            <MrbButton
                                t={t}
                                label="Add"
                                classNameOverride="c-btn c-btn--med c-btn--primary"
                                onClick={onSelectItem}
                                disabled={isAddContentButtonDisabled}
                            />
                            {selectedItemExists && (
                                <div className="c-device-menu__note c-device-menu__note--secondary u-mar--left--sml">
                                    {contentSelectStore.selectedItem.title} already selected.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <ContentPanel loadioStoreHomeSectionViewStore={loadioStoreHomeSectionViewStore} t={t} />
            </div>
        </div>
    );
});

const ContentPanel = observer(function ContentPanel({ loadioStoreHomeSectionViewStore, t }) {
    const { item, deleteItem } = loadioStoreHomeSectionViewStore;

    if (item.items.length === 0) return null;

    return <div className="c-device__list">{map(item.items, (item) => renderItem(item, deleteItem))}</div>;
});

function renderItem(item, onDelete) {
    if (!isNil(item.loadioArtist)) {
        return <LoadioArtist artist={item.loadioArtist} onDelete={() => onDelete(item.id)} />;
    } else if (!isNil(item.loadioAlbum)) {
        return <LoadioAlbum album={item.loadioAlbum} onDelete={() => onDelete(item.id)} />;
    }

    return null;
}

function LoadioArtist({ artist, onDelete }) {
    const rootStore = useRootStore();
    let coverImageUrl;
    if (artist.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createLoadioArtistCoverImageUrl(artist.coverImageId, 200, 200);
    }
    return (
        <div className="c-device__list__item">
            <Image src={coverImageUrl} />
            <p className="c-device__item__title">{artist.name}</p>
            <MrbButton
                className="c-btn c-btn--base c-btn--ghost c-device__btn"
                icon="u-icon u-icon--sml u-icon--close--primary"
                onClick={onDelete}
            />
        </div>
    );
}

function LoadioAlbum({ album, onDelete }) {
    const rootStore = useRootStore();
    let coverImageUrl;
    if (album.coverImageId) {
        coverImageUrl = rootStore.coverImageFactory.createLoadioAlbumCoverImageUrl(album.coverImageId, 200, 200);
    }
    return (
        <div className="c-device__list__item">
            <Image src={coverImageUrl} />
            <p className="c-device__item__title">{album.title}</p>
            <MrbButton
                className="c-btn c-btn--base c-btn--ghost c-device__btn"
                icon="u-icon u-icon--sml u-icon--close--primary"
                onClick={onDelete}
            />
        </div>
    );
}

const TitleInput = observer(function TitleInput({ item, onChange }) {
    return (
        <div className="u-width--100 u-width--300--max">
            <label className="u-type--sml">Section Title</label>
            <input
                className="c-input c-input--text c-input--med c-input--outline u-mar--top--tny"
                value={item.title}
                disabled={!item.isActive}
                onChange={onChange}
            />
        </div>
    );
});

function OptionLabel({ item }) {
    let icon = null;
    if (!isNil(item.loadioArtistId)) {
        icon = "loadio";
    } else if (!isNil(item.loadioAlbumId)) {
        icon = "loadio";
    }
    return (
        <span>
            <i className={`u-icon u-icon--med u-icon--${icon} u-mar--right--tny`} />
            {item.title}
            {item.isPremium && <i className="u-icon u-icon--med u-icon--premium u-mar--left--tny" />}
        </span>
    );
}

LoadioStoreHomeSectionTemplate.propTypes = {
    loadioStoreHomeSectionViewStore: PropTypes.object.isRequired,
};

export default observer(LoadioStoreHomeSectionTemplate);
